import { TDropdownItem, TLiftedFormModel, TUpdateFields, TLiftedInputTypes } from './form.model';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { EMPTY_INPUT } from './form.modifier';
import { FORM_UTILS } from './form.utils';

function isNumber(x: any): x is number {
  return typeof x === 'number';
}

function checkIsValidValue(x: string | number | string[] | number[] | undefined | TDropdownItem['value'][]) {
  if (x === undefined || x === null) return false;
  if (isNumber(x)) return true;
  return !!x.length;
}

export const TASK_FORM = {
  /**
   * create unique ids for each input
   * @param formModel
   * @returns formModel
   */
  creatUids: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => ({
        ...v,
        metadata: {
          ...v.metadata,
          id: 'randronId' + Math.random(), // TODO: Replace by better ramdon algo
        },
      })),
    };
  },
  /**
   * Create payload for API
   * @param formModel
   * @returns formModel
   */
  generatePayload: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    const payload = formModel.inputs
      .filter((v) => v.input.type !== 'EMPTY' && !v.metadata.removeFromPayload)
      .reduce((acc, v) => {
        const keyName = v.metadata.dependsOn?.updateFields?.pseudoKey || v.metadata.keyName;

        if (v.input.type === 'INPUT' && v.input.subType === 'date' && v.input.dateType) {
          let val = v.input.value;
          if (v.input.dateType === 'DATETIME') val = Date.parse(v.input.value as string);
          else if (val && v.input.dateType === 'YYYY-MM-DD') val = moment(v.input.value).format('YYYY-MM-DD');

          return {
            ...acc,
            [keyName]: val,
          };
        } else if (v.input.type === 'DROPDOWN' && v.input.inputValueForOtherOption) {
          return {
            ...acc,
            [keyName]: v.input.inputValueForOtherOption.input.value,
          };
        } else if (v.input.type === 'CHECKBOX') {
          return {
            ...acc,
            [keyName]: v.input.checked,
          };
        } else
          return {
            ...acc,
            [keyName]: v.input.value,
          };
      }, {});

    const filterUndefiedValues = Object.entries(payload)
      .filter((v) => v[1] !== undefined)
      .reduce((acc, v) => ({ ...acc, [v[0]]: v[1] }), {});
    return {
      ...formModel,
      payload: filterUndefiedValues,
    };
  },
  /**
   * Moving all the hidden fields to end to avoid breaking of rendered HTML
   * @param formModel
   * @returns formModel
   */
  moveHiddenToLast: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    const hiddeItems = formModel.inputs.filter((v) => v.metadata.isHidden);
    const items = formModel.inputs.filter((v) => !v.metadata.isHidden);
    return {
      ...formModel,
      inputs: [...items, ...hiddeItems],
    };
  },

  /**
   * Checks for Validity of the input in case the field is required and sets the isValid key accordingly.
   * @param formModel
   * @returns formModel
   */
  isValid: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    const { inputs } = formModel;
    const validatedInputs = inputs.map((v) => {
      const isRequired = v.metadata.isRequired;
      switch (v.input.type) {
        case 'INPUT':
        case 'TEXTAREA': {
          const isInputValid = checkIsValidValue(v.input.value);
          let isValid = isRequired ? isInputValid : true;
          if (isValid && isInputValid && v.input.type === 'INPUT') {
            switch (v.input.subType) {
              case 'number': {
                const numValue = Number(v.input.value);
                const minInclusive = v.input.isMinInclusive !== false; // default inclusive
                const maxInclusive = v.input.isMaxInclusive !== false; // default inclusive

                //Negative input validation
                isValid = numValue >= 0;
                //Min max validation, checking for undefined as min/max could be 0
                if (v.input.min !== undefined && v.input.max !== undefined)
                  isValid =
                    (minInclusive ? numValue >= v.input.min : numValue > v.input.min) &&
                    (maxInclusive ? numValue <= v.input.max : numValue < v.input.max);
                else if (v.input.min !== undefined)
                  isValid = minInclusive ? numValue >= v.input.min : numValue > v.input.min;
                else if (v.input.max !== undefined)
                  isValid = maxInclusive ? numValue <= v.input.max : numValue < v.input.max;

                break;
              }
            }
          }

          if (isValid && isInputValid && v.input.type === 'TEXTAREA') {
            if (v.input.minlength) isValid = v.input.value ? v.input.value.length >= v.input.minlength : false;
            if (v.input.maxlength !== undefined && isValid)
              isValid = v.input.value ? v.input.value.length <= v.input.maxlength : v.input.maxlength === 0;
          }

          return {
            ...v,
            metadata: {
              ...v.metadata,
              isValid,
            },
          };
        }
        case 'DROPDOWN':
        case 'DROPDOWN_WITH_SEARCH':
        case 'SEARCH': {
          const isInputValid = checkIsValidValue(v.input.value);
          const isValid = isRequired ? !!isInputValid : true;
          return {
            ...v,
            metadata: {
              ...v.metadata,
              isValid,
            },
            ...(v.input.type === 'DROPDOWN' &&
              v.input.inputValueForOtherOption && {
                input: {
                  ...v.input,
                  inputValueForOtherOption: {
                    ...v.input.inputValueForOtherOption,
                    metadata: {
                      ...v.input.inputValueForOtherOption.metadata,
                      isValid: v.input.inputValueForOtherOption.input.value !== undefined,
                    },
                  },
                },
              }),
          };
        }
        case 'CHECKBOX':
          return {
            ...v,
            metadata: {
              ...v.metadata,
              isValid: true,
            },
          };
        default:
          return { ...v };
      }
    });

    const validInputs = validatedInputs.filter((v) => v.input.type !== 'EMPTY');
    const nonValidItem = validInputs.find((v) => !v.metadata.isValid);

    return {
      ...formModel,
      inputs: [...validatedInputs],
      isValid: !nonValidItem,
    };
  },

  /**
   * Loops over the inputs and updates the dependent fields.
   * It also updates the formModel arg every time and then calls the updateADependentField method,
   * so that while updating the dependent fields it has the updated 'dependantOn' values which were
   * updated in the previous loops.
   * @param formModel
   * @returns formModel
   */
  updateDependentFields: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    const { inputs } = formModel;
    const updatedDependentFields = inputs.reduce((acc, cv, index) => {
      return TASK_FORM.isValid(TASK_FORM.updateADependentField(acc, cv, index));
    }, formModel);
    return {
      ...formModel,
      ...updatedDependentFields,
    };
  },

  /**
   * Finds the dependantOnInput for the currentInput and calls the methods 'getUpdatedDependentField' to get updatedInput
   * @param formModel
   * @param currentInput
   * @param index
   * @returns
   */
  updateADependentField: <T>(
    formModel: TLiftedFormModel<T>,
    currentInput: TLiftedFormModel<T>['inputs'][number],
    index: number,
  ): TLiftedFormModel<T> => {
    const { inputs } = formModel;

    // If currentInput is a dependent field
    if (currentInput.metadata.dependsOn !== undefined) {
      const dependantOnInput = inputs.find(
        ({ metadata }) => metadata.keyName === currentInput.metadata.dependsOn?.keyName,
      );

      //Update the currentInput only when dependantOnInput is currently active, to avoid the redundant loops.
      if (dependantOnInput && dependantOnInput.metadata.isActive) {
        const updatedDependentField = TASK_FORM.getUpdatedDependentField(dependantOnInput, currentInput);

        //Update the formModel inputs with the 'updatedDependentField' for index 'i'
        return {
          ...formModel,
          inputs: inputs.map((val, i) => {
            if (i === index) return { ...updatedDependentField };
            return val;
          }),
        };
      }
    }
    return formModel;
  },

  /**
   * Updates the 'isDisabled', 'label', 'value', 'options' properties if the dependentOn fields's value is valid.
   * @param dependantOnInput
   * @param currentInput
   * @returns
   */
  getUpdatedDependentField: <T>(
    dependantOnInput: TLiftedFormModel<T>['inputs'][number],
    currentInput: TLiftedFormModel<T>['inputs'][number],
  ): TLiftedFormModel<T>['inputs'][number] => {
    let updatedDependentField = cloneDeep(currentInput);

    //If dependantOnInput's value is not undefined and is valid, then update the current input
    if (dependantOnInput?.input.value !== undefined && dependantOnInput.metadata.isValid) {
      if (updatedDependentField.metadata.dependsOn?.valuesCallback) {
        const updateFields = updatedDependentField.metadata.dependsOn.valuesCallback(
          dependantOnInput.input.value,
          currentInput.input.value,
        );
        if (updateFields) {
          //update label and dependsOn.updateFields
          updatedDependentField = {
            ...updatedDependentField,
            metadata: {
              ...updatedDependentField.metadata,
              ...(updateFields.updatedLabel && { label: updateFields.updatedLabel }),
              ...(updateFields.updatedIsRequired !== undefined && { isRequired: updateFields.updatedIsRequired }),
              dependsOn: {
                ...updatedDependentField.metadata.dependsOn,
                updateFields,
              },
            },
          };

          if (
            updateFields.textArea?.updatedMinlength !== undefined &&
            updatedDependentField.input.type === 'TEXTAREA'
          ) {
            updatedDependentField = {
              ...updatedDependentField,
              input: {
                ...updatedDependentField.input,
                minlength: updateFields.textArea.updatedMinlength,
              },
            };
          }

          if (updateFields.updatedValues) {
            switch (currentInput.input.type) {
              case 'SEARCH':
                updatedDependentField = TASK_FORM.updateSearchType(updatedDependentField, updateFields.updatedValues);
                break;
              case 'DROPDOWN':
              case 'DROPDOWN_WITH_SEARCH':
                updatedDependentField = TASK_FORM.updateDropdownType(updatedDependentField, updateFields.updatedValues);
                break;
              case 'INPUT':
                updatedDependentField = TASK_FORM.updateInputType(updatedDependentField, updateFields.updatedValues);
                break;
              case 'TEXTAREA':
                updatedDependentField = TASK_FORM.updateTextAreaType(updatedDependentField, updateFields.updatedValues);
                break;
            }
          }
        }
      }
      //set isDisabled to false
      updatedDependentField = {
        ...updatedDependentField,
        metadata: {
          ...updatedDependentField.metadata,
          isDisabled:
            (updatedDependentField.input.type === 'DROPDOWN' && !updatedDependentField.input.options?.length) || false,
        },
      };
    } else {
      updatedDependentField = TASK_FORM.resetFormField(updatedDependentField);
    }
    return updatedDependentField;
  },

  /**
   * Update type TInput
   * @param existingInput
   * @param updatedValues
   * @returns
   */
  updateInputType: <T>(
    existingInput: TLiftedInputTypes<T>,
    updatedValues: TUpdateFields['updatedValues'],
  ): TLiftedInputTypes<T> => {
    if (updatedValues?.type === 'INPUT' && existingInput.input.type === 'INPUT')
      return {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: updatedValues?.value,
        },
      };
    else return existingInput;
  },

  /**
   * Update type TTextArea
   * @param existingInput
   * @param updatedValues
   * @returns
   */
  updateTextAreaType: <T>(
    existingInput: TLiftedInputTypes<T>,
    updatedValues: TUpdateFields['updatedValues'],
  ): TLiftedInputTypes<T> => {
    if (updatedValues?.type === 'TEXTAREA' && existingInput.input.type === 'TEXTAREA')
      return {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: updatedValues?.value,
        },
      };
    else return existingInput;
  },

  /**
   * Update type TSearch
   * @param existingInput
   * @param updatedValues
   * @returns
   */
  updateSearchType: <T>(
    existingInput: TLiftedInputTypes<T>,
    updatedValues: TUpdateFields['updatedValues'],
  ): TLiftedInputTypes<T> => {
    if (updatedValues?.type === 'SEARCH' && existingInput.input.type === 'SEARCH')
      return {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: updatedValues?.value || existingInput.input.value,
          options: updatedValues?.options,
          showSuggestions: !!updatedValues?.options?.length,
          searchResultsNotFound: !updatedValues?.options?.length,
        },
      };
    else return existingInput;
  },

  /**
   * Update type TDropdown
   * @param existingInput
   * @param updatedValues
   * @returns
   */
  updateDropdownType: <T>(
    existingInput: TLiftedInputTypes<T>,
    updatedValues: TUpdateFields['updatedValues'],
  ): TLiftedInputTypes<T> => {
    if (
      (updatedValues?.type === 'DROPDOWN' && existingInput.input.type === 'DROPDOWN') ||
      (updatedValues?.type === 'DROPDOWN_WITH_SEARCH' && existingInput.input.type === 'DROPDOWN_WITH_SEARCH')
    ) {
      const updatedInputValue = FORM_UTILS.getUpdatedDropdownValue(existingInput, updatedValues);

      return {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: updatedInputValue,
          options: updatedValues?.options,
        },
        metadata: {
          ...existingInput.metadata,
          isDisabled: !updatedValues?.options?.length,
        },
      };
    } else return existingInput;
  },

  /**
   * Resets the existingInput
   * @param existingInput
   * @returns
   */
  resetFormField: <T>(existingInput: TLiftedInputTypes<T>): TLiftedInputTypes<T> => {
    return {
      ...existingInput,
      input: {
        ...existingInput.input,
        value: undefined,
        ...(existingInput.input.type === 'SEARCH' && { searchText: undefined }),
      },
      metadata: {
        ...existingInput.metadata,
        isDisabled: true,
      },
    };
  },
  /**
   * Set Values in form feilds based on input setValue callback
   * @param formModel
   * @returns updatedFormModel
   */
  setValues: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => {
        switch (v.input.type) {
          case 'DROPDOWN':
          case 'DROPDOWN_WITH_SEARCH':
          case 'INPUT':
            return {
              ...v,
              input: {
                ...v.input,
                value: formModel.data && v.input.setValue?.(formModel.data),
              },
            } as TLiftedFormModel<T>['inputs'][number];

          default:
            return v;
        }
      }),
    };
  },

  /**
   * Set Options in dropdown value
   * @param formModel
   * @returns updateFormModel
   */
  setOptions: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => {
        switch (v.input.type) {
          case 'DROPDOWN': {
            const updatedOptionsOpt = formModel.data && v.input.setOptions?.(formModel.data);
            return {
              ...v,
              input: {
                ...v.input,
                options: updatedOptionsOpt || v.input.options,
              },
            } as TLiftedFormModel<T>['inputs'][number];
          }
          default:
            return v;
        }
      }),
    };
  },

  /**
   *  Set Checked propert for input type 'CHECKBOX
   * @param formModel
   * @returns updateFormModel
   */
  setChecked: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => {
        switch (v.input.type) {
          case 'CHECKBOX': {
            const updatedCheckedOpt = formModel.data && v.input.setChecked?.(formModel.data);
            return {
              ...v,
              input: {
                ...v.input,
                checked: updatedCheckedOpt || v.input.checked,
              },
            } as TLiftedFormModel<T>['inputs'][number];
          }
          default:
            return v;
        }
      }),
    };
  },

  /**
   * Mark all input types as touched
   * @param formModel
   * @returns formModel
   */
  setTouched: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => ({
        ...v,
        ...(v.input.type === 'DROPDOWN' &&
          v.input.inputValueForOtherOption && {
            input: {
              ...v.input,
              inputValueForOtherOption: {
                ...v.input.inputValueForOtherOption,
                metadata: {
                  ...v.input.inputValueForOtherOption.metadata,
                  isTouched: true,
                },
              },
            },
          }),
        metadata: {
          ...v.metadata,
          isTouched: true,
        },
      })),
    };
  },

  /**
   * Sets the value of noEditsInTable in the input metadata
   * @param formModel
   * @returns formModel
   */
  setNoEditsInEditMode: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return {
      ...formModel,
      inputs: formModel.inputs.map((v) => {
        const noEditsInTable =
          v.metadata.noEditsInTable || (formModel.data && v.metadata.setNoEditsInTable?.(formModel.data));
        if (noEditsInTable) return EMPTY_INPUT;

        return {
          ...v,
          metadata: {
            ...v.metadata,
            noEditsInTable: formModel.data && v.metadata.setNoEditsInTable?.(formModel.data),
          },
        };
      }),
    };
  },

  updateInputForOtherOption: <T>(curInput: TLiftedInputTypes<T>): TLiftedInputTypes<T> => {
    if (curInput.input.type === 'DROPDOWN') {
      //Setting inputValueForOtherOption for the first time when 'Other' got selected
      if (curInput.input.value === 'Other' && !curInput.input.inputValueForOtherOption) {
        return {
          ...curInput,
          input: {
            ...curInput.input,
            inputValueForOtherOption: {
              input: {
                type: 'INPUT',
                subType: 'text',
                placeholder: 'Other',
              },
              metadata: {
                keyName: 'otherInput',
                isRequired: true,
              },
            },
          },
        };
      }
      //If value other than 'Other' gets selcted, then remove inputValueForOtherOption
      else if (curInput.input.value !== 'Other' && curInput.input.inputValueForOtherOption) {
        return {
          ...curInput,
          input: {
            ...curInput.input,
            inputValueForOtherOption: undefined,
          },
        };
      }
      return curInput;
    }
    return curInput;
  },
};
